<template>
	<v-navigation-drawer v-if="open" permanent location="right" :order="2" :rail="mini" rail-width="48" width="360"
		mobile-breakpoint="0" color="panel_sub" theme="dark">
		<!-- 最小化時: 展開ボタンのみ -->
		<div v-if="mini" class="d-flex flex-column align-center pt-2">
			<v-btn icon variant="text" size="small" @click="mini = false">
				<v-icon>mdi-chevron-left</v-icon>
			</v-btn>
		</div>

		<!-- 展開時: 一覧本体 -->
		<div v-else class="pa-3">
			<div class="d-flex align-center justify-space-between mb-2">
				<span class="text-subtitle-1">提案経路一覧</span>
				<div>
					<v-btn icon variant="text" size="small" @click="mini = true">
						<v-icon>mdi-chevron-right</v-icon>
					</v-btn>
					<v-btn icon variant="text" size="small" @click="open = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</div>
			</div>

			<v-table v-if="candidates.length" density="compact">
				<thead>
					<tr>
						<th class="text-left">目的</th>
						<th v-for="k in metricKeys" :key="k" class="text-right">{{ metricLabels[k] }}</th>
						<th class="text-left">状態</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(c, i) in candidates" :key="i">
						<td class="text-left">{{ objectiveLabel(c.objective) }}</td>
						<td v-for="k in metricKeys" :key="k" class="text-right">{{ metricDisplay(c, k).text }}</td>
						<td class="text-left">
							<v-tooltip v-if="c.is_fallback" location="top">
								<template #activator="{ props }">
									<v-chip v-bind="props" size="x-small" color="warning" variant="tonal">裁量</v-chip>
								</template>
								<span>{{ violationSummary(c) }}</span>
							</v-tooltip>
							<v-chip v-else size="x-small" color="success" variant="tonal">適合</v-chip>
						</td>
						<td>
							<v-btn size="small" color="primary" :loading="registeringIndex === i"
								:disabled="registeringIndex >= 0" @click="register(i)">登録</v-btn>
						</td>
					</tr>
				</tbody>
			</v-table>
			<div v-else class="text-caption mb-2">表示できる候補がありません。</div>

			<div v-if="describedDiagnostics.length" class="mt-3">
				<div class="text-subtitle-2 mb-1">
					{{ candidates.length ? "診断情報" : "経路が見つからなかった理由" }}
				</div>
				<v-alert v-for="(d, i) in describedDiagnostics" :key="i" :type="d.level" variant="tonal" density="compact"
					class="mb-1">
					<div class="font-weight-medium">{{ d.title }}</div>
					<div v-if="d.detail" class="text-caption">{{ d.detail }}</div>
					<div v-if="d.hint" class="text-caption">対処: {{ d.hint }}</div>
				</v-alert>
			</div>
		</div>
	</v-navigation-drawer>
</template>

<script>
import { notifyError } from "@/utils/notificationUtils";
import { renderPrototypeRoad } from "../../utils/prototypeRoadAIUtils";
import {
	describeDiagnostic,
	formatMetricForMode,
	objectiveLabel,
	violationSummary,
} from "../../utils/roadAiDiagnostics";

export default {
	name: "RoadProposalListPanel",
	data: () => ({
		open: false,
		mini: false,
		registeringIndex: -1,
		metricKeys: ["cut", "fill", "over_cut", "over_fill", "cf_diff"],
		metricLabels: {
			cut: "切土",
			fill: "盛土",
			over_cut: "掘過",
			over_fill: "盛過",
			cf_diff: "切盛差",
		},
	}),
	computed: {
		candidates() {
			return this.$store.getters["addRoadTerrainByAi/candidates"] || [];
		},
		diagnostics() {
			return this.$store.getters["addRoadTerrainByAi/diagnostics"] || [];
		},
		mode() {
			return this.$store.getters["addRoadTerrainByAi/mode"] || null;
		},
		describedDiagnostics() {
			return this.diagnostics.map((d) => describeDiagnostic(d));
		},
	},
	watch: {
		// 実行完了で候補が更新されたら自動表示（再実行時も再表示・置換）
		candidates() {
			this.autoOpen();
		},
		// 0件時は診断（理由）だけが更新されるため、こちらでも自動表示する
		diagnostics() {
			this.autoOpen();
		},
	},
	methods: {
		objectiveLabel,
		violationSummary,
		// 点群のみモードの設計依存指標は「対象外」と表示する（AC-7）。
		metricDisplay(candidate, key) {
			return formatMetricForMode(candidate, key, this.mode);
		},
		autoOpen() {
			if (this.candidates.length || this.diagnostics.length) {
				this.open = true;
				this.mini = false;
			}
		},
		register(i) {
			const candidate = this.candidates[i];
			if (!candidate || !Array.isArray(candidate.points)) return;
			// 実行に実際に使われた点群アセットへ関連付ける（AC-15）。
			// v2-server は asset_id 無しの点群に "pc-<id>" 形式を返し得るため、
			// 数値化できない場合は表示中の点群へフォールバックする。
			const usedSource =
				this.$store.getters["addRoadTerrainByAi/usedSource"] || null;
			const rawAssetId = usedSource?.pointCloudAssetId;
			const usedAssetId = rawAssetId ? Number(rawAssetId) : Number.NaN;
			const pointCloudAssetId = Number.isFinite(usedAssetId)
				? usedAssetId
				: this.$store.getters.activePointCloud?.getAssetIdNum();
			if (pointCloudAssetId == null || Number.isNaN(pointCloudAssetId)) {
				notifyError(
					this,
					"点群が選択されていません。点群を表示してから登録してください。",
				);
				return;
			}
			this.registeringIndex = i;
			// デフォルト名は「仮設道路AI_<目的日本語ラベル>」（連番なし）。
			const roadName = `仮設道路AI_${objectiveLabel(candidate.objective)}`;
			// 幅員は入力値（道路幅員）を左右均等に割り当て、他はデフォルト。
			const roadWidthM =
				this.$store.getters["addRoadTerrainByAi/roadWidthM"] || 4.0;
			const halfWidth = roadWidthM / 2;
			renderPrototypeRoad(candidate.points, pointCloudAssetId, (id) => {
				// プロパティダイアログを開かず、非表示ダイアログ経由でデフォルト値のまま登録する。
				const event = new CustomEvent("registerAiRoad", {
					detail: {
						id,
						roadName,
						leftWidth: halfWidth,
						rightWidth: halfWidth,
					},
				});
				window.dispatchEvent(event);
			})
				.catch((e) => {
					console.error(e);
					notifyError(this, e?.message || "道路の登録に失敗しました。");
				})
				.finally(() => {
					this.registeringIndex = -1;
				});
		},
	},
};
</script>
